import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import {
  getCatalog,
  createTrack,
  updateTrack,
  putTrackSteps,
  getQuiz,
  type CatalogTrack,
  type TrackMetaInput,
  type StepInput,
} from '../lib/api';
import type { ContentBlock, Slide, Step, StepType } from '../types';

// Khalifa "Trails" authoring view. Lists ALL tracks (from /api/catalog) and
// provides a create/edit surface for track metadata and an ordered step editor.
// After every successful mutation the relevant state is refreshed. 403 → sticky
// forbidden (mirrors AdminPeople/AdminInvites pattern).

// ── Internal editor types ─────────────────────────────────────────────────────

type LocalRef = { _key: string; label: string; url: string };

type LocalSlide = {
  _key: string;
  src: string;
  alt: string;
  caption: string;
};

type LocalQuestion = {
  _key: string;
  id?: string;
  prompt: string;
  options: string[];
  /** null = admin must re-select (existing step loaded from server without answers) */
  answer: number | null;
};

type LocalStep = {
  _key: string;
  id?: string;
  type: StepType;
  title: string;
  points: number;
  references: LocalRef[];
  // video
  vimeoId: string;
  vimeoHash: string;
  youtubeId: string;
  mediaSrc: string;
  bunnyVideoId: string;
  videoSource: 'vimeo' | 'youtube' | 'studio';
  // external
  extUrl: string;
  extPreviewTitle: string;
  extPreviewDesc: string;
  extPreviewImage: string;
  evidence: 'url' | 'file' | 'none';
  // reading
  contentText: string;
  // quiz
  questions: LocalQuestion[];
  // slides
  slides: LocalSlide[];
  pdfUrl: string;
};

// ── Helpers ───────────────────────────────────────────────────────────────────

let _kc = 0;
const newKey = () => `k${++_kc}`;

function blankStep(type: StepType): LocalStep {
  return {
    _key: newKey(),
    type,
    title: '',
    points: 10,
    references: [],
    vimeoId: '',
    vimeoHash: '',
    youtubeId: '',
    mediaSrc: '',
    bunnyVideoId: '',
    videoSource: 'vimeo',
    extUrl: '',
    extPreviewTitle: '',
    extPreviewDesc: '',
    extPreviewImage: '',
    evidence: 'none',
    contentText: '',
    questions: [],
    slides: [],
    pdfUrl: '',
  };
}

function contentBlocksToText(blocks: ContentBlock[]): string {
  return blocks
    .map((b) => {
      if (b.kind === 'heading') return `# ${b.text}`;
      if (b.kind === 'para') return b.text;
      if (b.kind === 'callout') return `> ${b.text}`;
      if (b.kind === 'bullets') return b.items.map((i) => `- ${i}`).join('\n');
      if (b.kind === 'image')
        return `![${b.alt}](${b.src})${b.caption ? ` "${b.caption}"` : ''}`;
      if (b.kind === 'path') return '[[your-path]]';
      if (b.kind === 'steps' || b.kind === 'cards')
        return [`::${b.kind}`, ...b.items.map((it) => `- ${it.title} | ${it.text}`)].join('\n');
      return '';
    })
    .join('\n\n');
}

function parseContentText(text: string): ContentBlock[] {
  const blocks: ContentBlock[] = [];
  const chunks = text.split(/\n\n+/);
  for (const chunk of chunks) {
    const t = chunk.trim();
    if (!t) continue;
    const lines = t.split('\n');
    if (t === '[[your-path]]') {
      blocks.push({ kind: 'path' });
    } else if ((lines[0] === '::steps' || lines[0] === '::cards') && lines.length > 1) {
      // "::steps" or "::cards", then one "- Title | text" line per item.
      const items = lines.slice(1).map((l) => {
        const [title, ...rest] = l.replace(/^- /, '').split(' | ');
        return { title: title.trim(), text: rest.join(' | ').trim() };
      });
      blocks.push({ kind: lines[0] === '::steps' ? 'steps' : 'cards', items });
    } else if (lines.every((l) => l.startsWith('- '))) {
      blocks.push({ kind: 'bullets', items: lines.map((l) => l.slice(2).trim()) });
    } else if (lines.length === 1 && t.startsWith('# ')) {
      blocks.push({ kind: 'heading', text: t.slice(2).trim() });
    } else if (lines.length === 1 && t.startsWith('> ')) {
      blocks.push({ kind: 'callout', text: t.slice(2).trim() });
    } else {
      blocks.push({ kind: 'para', text: t });
    }
  }
  return blocks;
}

function stepFromCatalog(s: Step): LocalStep {
  return {
    _key: newKey(),
    id: s.id,
    type: s.type,
    title: s.title,
    points: s.points,
    references: (s.references ?? []).map((r) => ({ _key: newKey(), ...r })),
    vimeoId: s.vimeoId ?? '',
    vimeoHash: s.vimeoHash ?? '',
    youtubeId: s.youtubeId ?? '',
    mediaSrc: s.mediaSrc ?? '',
    bunnyVideoId: s.bunnyVideoId ?? '',
    videoSource: s.mediaSrc ? 'studio' : s.youtubeId ? 'youtube' : 'vimeo',
    extUrl: s.url ?? '',
    extPreviewTitle: s.preview?.title ?? '',
    extPreviewDesc: s.preview?.description ?? '',
    extPreviewImage: s.preview?.image ?? '',
    evidence: s.evidence ?? 'none',
    contentText: contentBlocksToText(s.content ?? []),
    questions: [], // quiz questions fetched separately (server never returns answers in catalog)
    slides: (s.slides ?? []).map((sl: Slide) => ({
      _key: newKey(),
      src: sl.src,
      alt: sl.alt ?? '',
      caption: sl.caption ?? '',
    })),
    pdfUrl: s.pdfUrl ?? '',
  };
}

function toStepInput(s: LocalStep): StepInput {
  const validRefs = s.references.filter((r) => r.label && r.url);
  const base: StepInput = {
    ...(s.id ? { id: s.id } : {}),
    type: s.type,
    title: s.title,
    points: s.points,
    ...(validRefs.length > 0
      ? { references: validRefs.map(({ label, url }) => ({ label, url })) }
      : {}),
  };

  if (s.type === 'video') {
    if (s.videoSource === 'studio') {
      return { ...base, mediaSrc: s.mediaSrc, ...(s.bunnyVideoId ? { bunnyVideoId: s.bunnyVideoId } : {}) };
    }
    return {
      ...base,
      ...(s.videoSource === 'vimeo'
        ? {
            vimeoId: s.vimeoId || 'REPLACE_ME',
            ...(s.vimeoHash && s.vimeoId && /^\d+$/.test(s.vimeoId)
              ? { vimeoHash: s.vimeoHash }
              : {}),
          }
        : { youtubeId: s.youtubeId }),
    };
  }

  if (s.type === 'external') {
    const preview = {
      ...(s.extPreviewTitle ? { title: s.extPreviewTitle } : {}),
      ...(s.extPreviewDesc ? { description: s.extPreviewDesc } : {}),
      ...(s.extPreviewImage ? { image: s.extPreviewImage } : {}),
    };
    return {
      ...base,
      ...(s.extUrl ? { url: s.extUrl } : {}),
      evidence: s.evidence,
      ...(Object.keys(preview).length > 0 ? { preview } : {}),
    };
  }

  if (s.type === 'reading') {
    return { ...base, content: parseContentText(s.contentText) };
  }

  if (s.type === 'quiz') {
    return {
      ...base,
      questions: s.questions.map((q) => {
        // Defensive: a null (unconfirmed) answer must NEVER be silently
        // submitted as a real index. The save guard blocks this earlier, so
        // reaching here with null is a bug — fail loud rather than send 0.
        if (q.answer === null) {
          throw new Error('quiz_answer_unconfirmed');
        }
        return {
          ...(q.id ? { id: q.id } : {}),
          prompt: q.prompt,
          options: q.options,
          answer: q.answer,
        };
      }),
    };
  }

  if (s.type === 'slides') {
    // Drop any slide whose src is not a valid http(s)/whitelisted string.
    const validSlides = s.slides.filter((sl) => SLIDE_SRC_RE.test(sl.src));
    const validPdf = s.pdfUrl.startsWith('https://');
    // Belt-and-suspenders: handleStepsSave pre-scans for this and blocks with a
    // per-step error, so we should never reach here empty. Fail loud rather than
    // send a malformed/empty slides step to the server.
    if (validSlides.length === 0 && !validPdf) {
      throw new Error('slides_empty');
    }
    return {
      ...base,
      ...(validSlides.length > 0
        ? {
            slides: validSlides.map(({ src, alt, caption }) => ({
              src,
              ...(alt ? { alt } : {}),
              ...(caption ? { caption } : {}),
            })),
          }
        : {}),
      ...(validPdf ? { pdfUrl: s.pdfUrl } : {}),
    };
  }

  return base;
}

/** Parse server step-level error code e.g. "step_2_bad_vimeoId" → {index, reason}. */
function parseStepError(error: string): { index: number; reason: string } | null {
  const m = error.match(/^step_(\d+)_(.+)$/);
  if (!m) return null;
  return { index: parseInt(m[1]), reason: m[2] };
}

// ── StepRow subcomponent ──────────────────────────────────────────────────────

const STEP_TYPES: { value: StepType; label: string }[] = [
  { value: 'video', label: 'Video' },
  { value: 'external', label: 'External' },
  { value: 'reading', label: 'Reading' },
  { value: 'quiz', label: 'Quiz' },
  { value: 'slides', label: 'Slides' },
];

const INPUT_CLS =
  'w-full rounded-lg border border-rule bg-paper-0 px-3 py-1.5 text-sm text-ink-900 placeholder:text-ink-300 focus:outline-none focus:ring-1 focus:ring-terracotta-500 dark:border-clay-rule dark:bg-clay-floor dark:text-paper-50 dark:placeholder:text-ink-500';

interface StepRowProps {
  step: LocalStep;
  idx: number;
  totalCount: number;
  stepError?: string;
  trackId: string;
  onChange: (patch: Partial<LocalStep>) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  onAddQuestion: () => void;
  onRemoveQuestion: (qIdx: number) => void;
  onUpdateQuestion: (qIdx: number, patch: Partial<LocalQuestion>) => void;
  onAddRef: () => void;
  onRemoveRef: (rIdx: number) => void;
  onUpdateRef: (rIdx: number, patch: Partial<LocalRef>) => void;
}

function StepRow({
  step,
  idx,
  totalCount,
  stepError,
  trackId,
  onChange,
  onMoveUp,
  onMoveDown,
  onRemove,
  onAddQuestion,
  onRemoveQuestion,
  onUpdateQuestion,
  onAddRef,
  onRemoveRef,
  onUpdateRef,
}: StepRowProps) {
  return (
    <div
      className={`rounded-xl border bg-paper-50 p-3 dark:bg-clay-floor ${
        stepError ? 'border-terracotta-400' : 'border-rule dark:border-clay-rule'
      }`}
    >
      {/* Header row: index · type · title · points · controls */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-paper-100 text-xs font-semibold text-ink-500 dark:bg-clay-raised dark:text-paper-300">
          {idx + 1}
        </span>

        <select
          value={step.type}
          onChange={(e) => onChange({ type: e.target.value as StepType })}
          className="rounded-lg border border-rule bg-paper-0 px-2 py-1 text-xs text-ink-700 focus:outline-none focus:ring-1 focus:ring-terracotta-500 dark:border-clay-rule dark:bg-clay-surface dark:text-paper-200"
        >
          {STEP_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>

        <input
          type="text"
          value={step.title}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Paintra title (required)"
          className="min-w-0 flex-1 rounded-lg border border-rule bg-paper-0 px-3 py-1 text-sm text-ink-900 placeholder:text-ink-300 focus:outline-none focus:ring-1 focus:ring-terracotta-500 dark:border-clay-rule dark:bg-clay-floor dark:text-paper-50 dark:placeholder:text-ink-500"
        />

        <div className="flex items-center gap-1">
          <input
            type="number"
            value={step.points}
            onChange={(e) => onChange({ points: parseInt(e.target.value) || 10 })}
            className="w-14 rounded-lg border border-rule bg-paper-0 px-2 py-1 text-sm text-ink-700 focus:outline-none focus:ring-1 focus:ring-terracotta-500 dark:border-clay-rule dark:bg-clay-floor dark:text-paper-200"
            title="Points awarded for this paintra"
          />
          <span className="text-xs text-ink-400">pts</span>
        </div>

        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={idx === 0}
            className="rounded p-0.5 text-ink-400 hover:bg-paper-100 disabled:opacity-25 dark:hover:bg-clay-raised"
            title="Move up"
          >
            <ChevronUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={idx === totalCount - 1}
            className="rounded p-0.5 text-ink-400 hover:bg-paper-100 disabled:opacity-25 dark:hover:bg-clay-raised"
            title="Move down"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => {
              if (window.confirm(`Remove paintra ${idx + 1}: "${step.title || 'untitled'}"?`))
                onRemove();
            }}
            className="rounded p-0.5 text-terracotta-400 hover:bg-terracotta-50 dark:hover:bg-terracotta-900/20"
            title="Remove paintra"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {stepError && (
        <p className="mt-1 text-xs text-terracotta-700 dark:text-terracotta-400">
          Server error: {stepError.replace(/_/g, ' ')}
        </p>
      )}

      {/* Type-specific fields */}
      <div className="mt-3">
        {step.type === 'video' && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-ink-500 dark:text-paper-300">Source:</span>
              {(['vimeo', 'youtube', 'studio'] as const).map((src) => (
                <button
                  key={src}
                  type="button"
                  onClick={() => onChange({ videoSource: src })}
                  className={`rounded-md px-2 py-0.5 text-xs font-medium transition ${
                    step.videoSource === src
                      ? 'bg-terracotta-500 text-white'
                      : 'border border-rule text-ink-500 hover:bg-paper-100 dark:border-clay-rule dark:hover:bg-clay-raised'
                  }`}
                >
                  {src === 'vimeo' ? 'Vimeo' : src === 'youtube' ? 'YouTube' : 'Studio cut'}
                </button>
              ))}
            </div>

            {step.videoSource === 'studio' ? (
              <div>
                <label className="block text-xs text-ink-400">Studio file (same-origin mp4)</label>
                <input
                  type="text"
                  value={step.mediaSrc}
                  onChange={(e) => onChange({ mediaSrc: e.target.value })}
                  placeholder="/content/studio/objects.mp4"
                  className={`mt-1 ${INPUT_CLS}`}
                />
              </div>
            ) : step.videoSource === 'vimeo' ? (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-ink-400">Vimeo ID (digits, or REPLACE_ME)</label>
                  <input
                    type="text"
                    value={step.vimeoId}
                    onChange={(e) => onChange({ vimeoId: e.target.value })}
                    placeholder="123456789"
                    className={`mt-1 ${INPUT_CLS}`}
                  />
                </div>
                <div>
                  <label className="block text-xs text-ink-400">
                    Vimeo hash{' '}
                    <span className="text-ink-300">(unlisted videos only)</span>
                  </label>
                  <input
                    type="text"
                    value={step.vimeoHash}
                    onChange={(e) => onChange({ vimeoHash: e.target.value })}
                    placeholder="6-20 alnum, optional"
                    className={`mt-1 ${INPUT_CLS}`}
                  />
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-xs text-ink-400">YouTube ID (11 chars)</label>
                <input
                  type="text"
                  value={step.youtubeId}
                  onChange={(e) => onChange({ youtubeId: e.target.value })}
                  placeholder="dQw4w9WgXcQ"
                  className={`mt-1 ${INPUT_CLS}`}
                />
              </div>
            )}
          </div>
        )}

        {step.type === 'external' && (
          <div className="flex flex-col gap-2">
            <div>
              <label className="block text-xs text-ink-400">URL</label>
              <input
                type="url"
                value={step.extUrl}
                onChange={(e) => onChange({ extUrl: e.target.value })}
                placeholder="https://example.com"
                className={`mt-1 ${INPUT_CLS}`}
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs text-ink-400">Evidence required:</span>
              {(['none', 'url', 'file'] as const).map((ev) => (
                <label
                  key={ev}
                  className="flex cursor-pointer items-center gap-1 text-xs text-ink-600 dark:text-paper-300"
                >
                  <input
                    type="radio"
                    name={`evidence-${step._key}`}
                    value={ev}
                    checked={step.evidence === ev}
                    onChange={() => onChange({ evidence: ev })}
                    className="accent-terracotta-500"
                  />
                  {ev}
                </label>
              ))}
            </div>
            <details>
              <summary className="cursor-pointer text-xs text-ink-400 hover:text-ink-600">
                Preview metadata (optional)
              </summary>
              <div className="mt-2 flex flex-col gap-2">
                <input
                  type="text"
                  value={step.extPreviewTitle}
                  onChange={(e) => onChange({ extPreviewTitle: e.target.value })}
                  placeholder="Preview title"
                  className={INPUT_CLS}
                />
                <input
                  type="text"
                  value={step.extPreviewDesc}
                  onChange={(e) => onChange({ extPreviewDesc: e.target.value })}
                  placeholder="Preview description"
                  className={INPUT_CLS}
                />
                <input
                  type="url"
                  value={step.extPreviewImage}
                  onChange={(e) => onChange({ extPreviewImage: e.target.value })}
                  placeholder="Preview image (https://…)"
                  className={INPUT_CLS}
                />
              </div>
            </details>
          </div>
        )}

        {step.type === 'reading' && (
          <div>
            <label className="block text-xs text-ink-400">
              Content — blank line = new paragraph &nbsp;·&nbsp;{' '}
              <code className="rounded bg-paper-100 px-1 dark:bg-clay-raised"># </code> heading
              &nbsp;·&nbsp;{' '}
              <code className="rounded bg-paper-100 px-1 dark:bg-clay-raised">- </code> bullets
              &nbsp;·&nbsp;{' '}
              <code className="rounded bg-paper-100 px-1 dark:bg-clay-raised">&gt; </code> callout
            </label>
            <textarea
              value={step.contentText}
              onChange={(e) => onChange({ contentText: e.target.value })}
              rows={7}
              placeholder={
                '# Section heading\n\nParagraph text here.\n\n- Bullet item one\n- Bullet item two\n\n> This is a callout note.'
              }
              className={`mt-1 ${INPUT_CLS} font-mono text-xs`}
            />
          </div>
        )}

        {step.type === 'quiz' && (
          <div className="flex flex-col gap-3">
            {step.questions.some((q) => q.answer === null) && (
              <div className="rounded-lg border border-terracotta-300/60 bg-terracotta-50/60 px-3 py-2 text-xs text-terracotta-700 dark:border-terracotta-700/40 dark:bg-terracotta-900/20 dark:text-terracotta-300">
                Re-confirm the correct answer for each question — answers are not stored in
                the catalog and must be re-selected before saving.
              </div>
            )}

            {step.questions.map((q, qIdx) => (
              <div
                key={q._key}
                className="rounded-lg border border-rule bg-paper-0 p-3 dark:border-clay-rule dark:bg-clay-surface"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-ink-500 dark:text-paper-300">
                    Q{qIdx + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => onRemoveQuestion(qIdx)}
                    className="text-xs text-terracotta-500 hover:underline"
                  >
                    Remove
                  </button>
                </div>

                <input
                  type="text"
                  value={q.prompt}
                  onChange={(e) => onUpdateQuestion(qIdx, { prompt: e.target.value })}
                  placeholder="Question prompt"
                  className={`mt-2 ${INPUT_CLS}`}
                />

                <div className="mt-2 flex flex-col gap-1.5">
                  {q.options.map((opt, oIdx) => (
                    <div key={oIdx} className="flex items-center gap-2">
                      <input
                        type="radio"
                        name={`answer-${step._key}-q${qIdx}`}
                        checked={q.answer === oIdx}
                        onChange={() => onUpdateQuestion(qIdx, { answer: oIdx })}
                        className="accent-terracotta-500 shrink-0"
                        title="Mark as correct answer"
                      />
                      <input
                        type="text"
                        value={opt}
                        onChange={(e) => {
                          const newOpts = [...q.options];
                          newOpts[oIdx] = e.target.value;
                          onUpdateQuestion(qIdx, { options: newOpts });
                        }}
                        placeholder={`Option ${oIdx + 1}`}
                        className="flex-1 rounded-lg border border-rule bg-paper-0 px-2 py-1 text-sm text-ink-900 placeholder:text-ink-300 focus:outline-none focus:ring-1 focus:ring-terracotta-500 dark:border-clay-rule dark:bg-clay-floor dark:text-paper-50 dark:placeholder:text-ink-500"
                      />
                      {q.options.length > 2 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newOpts = q.options.filter((_, i) => i !== oIdx);
                            const newAnswer =
                              q.answer === null
                                ? null
                                : oIdx < q.answer
                                  ? q.answer - 1
                                  : q.answer === oIdx
                                    ? null
                                    : q.answer;
                            onUpdateQuestion(qIdx, { options: newOpts, answer: newAnswer });
                          }}
                          className="shrink-0 text-sm text-ink-400 hover:text-terracotta-500"
                          title="Remove option"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {q.answer === null && (
                  <p className="mt-1.5 text-xs font-medium text-terracotta-600 dark:text-terracotta-400">
                    Select the correct answer above.
                  </p>
                )}

                <button
                  type="button"
                  onClick={() =>
                    onUpdateQuestion(qIdx, { options: [...q.options, ''] })
                  }
                  className="mt-2 text-xs text-ink-400 hover:text-terracotta-600"
                >
                  + option
                </button>
              </div>
            ))}

            <button
              type="button"
              onClick={onAddQuestion}
              className="inline-flex items-center gap-1 self-start rounded-lg border border-rule px-3 py-1 text-xs font-medium text-ink-500 hover:bg-paper-100 dark:border-clay-rule dark:hover:bg-clay-raised"
            >
              <Plus className="h-3 w-3" /> Add question
            </button>
          </div>
        )}

        {step.type === 'slides' && (
          <SlidesEditor
            slides={step.slides}
            pdfUrl={step.pdfUrl}
            trackId={trackId}
            onChangeSlides={(slides) => onChange({ slides })}
            onChangePdfUrl={(pdfUrl) => onChange({ pdfUrl })}
          />
        )}
      </div>

      {/* References */}
      <div className="mt-3 border-t border-rule pt-3 dark:border-clay-rule">
        <p className="text-xs font-medium text-ink-400">Further reading</p>
        {step.references.length > 0 && (
          <div className="mt-1.5 flex flex-col gap-1.5">
            {step.references.map((ref, rIdx) => (
              <div key={ref._key} className="flex items-center gap-2">
                <input
                  type="text"
                  value={ref.label}
                  onChange={(e) => onUpdateRef(rIdx, { label: e.target.value })}
                  placeholder="Label"
                  className="w-28 rounded-lg border border-rule bg-paper-0 px-2 py-1 text-xs text-ink-900 placeholder:text-ink-300 focus:outline-none focus:ring-1 focus:ring-terracotta-500 dark:border-clay-rule dark:bg-clay-floor dark:text-paper-50 dark:placeholder:text-ink-500"
                />
                <input
                  type="url"
                  value={ref.url}
                  onChange={(e) => onUpdateRef(rIdx, { url: e.target.value })}
                  placeholder="https://…"
                  className="flex-1 rounded-lg border border-rule bg-paper-0 px-2 py-1 text-xs text-ink-900 placeholder:text-ink-300 focus:outline-none focus:ring-1 focus:ring-terracotta-500 dark:border-clay-rule dark:bg-clay-floor dark:text-paper-50 dark:placeholder:text-ink-500"
                />
                <button
                  type="button"
                  onClick={() => onRemoveRef(rIdx)}
                  className="shrink-0 text-ink-400 hover:text-terracotta-500"
                  title="Remove reference"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={onAddRef}
          className="mt-1.5 text-xs text-ink-400 hover:text-terracotta-600"
        >
          + reference
        </button>
      </div>
    </div>
  );
}

// ── SlidesEditor ─────────────────────────────────────────────────────────────

const ALLOWED_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp']);
const ALLOWED_PDF_TYPES = new Set(['application/pdf']);
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const SAFE_NAME_RE = /^[A-Za-z0-9._-]{1,128}$/;
// Mirrors StepView's IMG_SRC_RE — accepts http(s) or an in-repo /content|/images|/assets path.
const SLIDE_SRC_RE = /^(https?:\/\/[^\s]+|\/(content|images|assets)\/[^\s]+)$/i;

/**
 * Build a storage-safe filename matching the rule ^[A-Za-z0-9._-]{1,128}$.
 * Disallowed chars → '-'; a short UUID prefix avoids collisions. If the
 * sanitized base is empty (filename was all-disallowed chars), fall back to a
 * UUID-only name so the result is always non-empty and valid.
 */
function makeSafeFilename(raw: string): string {
  const sanitized = raw.replace(/[^A-Za-z0-9._-]/g, '-');
  const name =
    sanitized.length > 0
      ? `${crypto.randomUUID().slice(0, 8)}-${sanitized}`
      : crypto.randomUUID();
  return name.slice(0, 128);
}

function SlidesEditor({
  slides,
  pdfUrl,
  trackId,
  onChangeSlides,
  onChangePdfUrl,
}: {
  slides: LocalSlide[];
  pdfUrl: string;
  trackId: string;
  onChangeSlides: (slides: LocalSlide[]) => void;
  onChangePdfUrl: (url: string) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [urlInput, setUrlInput] = useState('');
  // Guard against setState-after-unmount if the admin navigates away mid-upload.
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const handleFileUpload = async (file: File, target: 'image' | 'pdf') => {
    setUploadError(null);

    // Target-specific type gate: the image picker accepts ONLY raster images, the
    // PDF picker ONLY a PDF. Reject a mismatch (e.g. a PDF chosen in the image
    // picker) before uploading.
    const allowed = target === 'pdf' ? ALLOWED_PDF_TYPES : ALLOWED_IMAGE_TYPES;
    if (!allowed.has(file.type)) {
      setUploadError(
        target === 'pdf'
          ? 'That is not a PDF — choose a PDF file.'
          : 'That is not a supported image — use PNG, JPEG, GIF, or WebP (no SVG/PDF).',
      );
      return;
    }
    // Match the server rule's `size < 10MB` (reject when >=).
    if (file.size >= MAX_UPLOAD_BYTES) {
      setUploadError('File too large — must be under 10 MB.');
      return;
    }

    // Build + validate the storage name BEFORE uploading so an invalid name fails
    // clearly here rather than as a confusing storage/unauthorized later.
    const safeName = makeSafeFilename(file.name);
    if (!SAFE_NAME_RE.test(safeName)) {
      setUploadError('Could not build a safe file name — rename the file and try again.');
      return;
    }

    setUploading(true);
    try {
      const storageRef = ref(storage, `tracks/${trackId}/slides/${safeName}`);
      // Race the upload against a 30 s timeout so the button never stays stuck
      // when Firebase Storage is unreachable (e.g. not yet enabled on the project).
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const timeoutPromise = new Promise<never>((_, rej) => {
        timeoutId = setTimeout(() => rej(new Error('upload_timeout')), 30_000);
      });
      await Promise.race([
        uploadBytes(storageRef, file, { contentType: file.type }),
        timeoutPromise,
      ]);
      clearTimeout(timeoutId);
      const downloadUrl = await getDownloadURL(storageRef);
      if (target === 'pdf') {
        onChangePdfUrl(downloadUrl);
      } else {
        onChangeSlides([...slides, { _key: newKey(), src: downloadUrl, alt: '', caption: '' }]);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '';
      const code = (err as { code?: string })?.code ?? '';
      const storageUnavailable =
        msg === 'upload_timeout' ||
        code === 'storage/retry-limit-exceeded' ||
        code === 'storage/unknown' ||
        code === 'storage/bucket-not-found' ||
        code === 'storage/unauthorized';
      if (mountedRef.current) {
        if (storageUnavailable) {
          setUploadError(
            'Upload failed — Firebase Storage may not be enabled yet (ask an admin to enable it), or the file/permissions were rejected. You can paste an image/PDF URL instead for now.',
          );
        } else {
          setUploadError(`Upload failed — ${code || msg || 'unknown error'}.`);
        }
      }
    } finally {
      if (mountedRef.current) setUploading(false);
    }
  };

  const addByUrl = () => {
    const src = urlInput.trim();
    if (!src.startsWith('https://')) {
      setUploadError('Paste a valid https:// image URL.');
      return;
    }
    onChangeSlides([...slides, { _key: newKey(), src, alt: '', caption: '' }]);
    setUrlInput('');
    setUploadError(null);
  };

  const removeSlide = (idx: number) => onChangeSlides(slides.filter((_, i) => i !== idx));

  const updateSlide = (idx: number, patch: Partial<LocalSlide>) =>
    onChangeSlides(slides.map((s, i) => (i === idx ? { ...s, ...patch } : s)));

  const moveSlideUp = (idx: number) => {
    if (idx === 0) return;
    const next = [...slides];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onChangeSlides(next);
  };

  const moveSlideDown = (idx: number) => {
    if (idx >= slides.length - 1) return;
    const next = [...slides];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    onChangeSlides(next);
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Slide list */}
      {slides.length > 0 && (
        <div className="flex flex-col gap-2">
          {slides.map((slide, idx) => (
            <div
              key={slide._key}
              className="flex items-start gap-2 rounded-lg border border-rule bg-paper-0 p-2 dark:border-clay-rule dark:bg-clay-surface"
            >
              {/* Thumbnail */}
              <div className="h-14 w-20 shrink-0 overflow-hidden rounded-md border border-rule dark:border-clay-rule">
                {/^https?:\/\//.test(slide.src) ? (
                  <img
                    src={slide.src}
                    alt={slide.alt || 'slide preview'}
                    referrerPolicy="no-referrer"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center bg-paper-100 text-[10px] text-ink-400 dark:bg-clay-raised">
                    img
                  </div>
                )}
              </div>

              {/* Alt + caption inputs */}
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <input
                  type="text"
                  value={slide.alt}
                  onChange={(e) => updateSlide(idx, { alt: e.target.value })}
                  placeholder="Alt text"
                  className={`${INPUT_CLS} text-xs`}
                />
                <input
                  type="text"
                  value={slide.caption}
                  onChange={(e) => updateSlide(idx, { caption: e.target.value })}
                  placeholder="Caption (optional)"
                  className={`${INPUT_CLS} text-xs`}
                />
              </div>

              {/* Move / remove controls */}
              <div className="flex shrink-0 flex-col items-center gap-0.5">
                <button
                  type="button"
                  onClick={() => moveSlideUp(idx)}
                  disabled={idx === 0}
                  className="rounded p-0.5 text-ink-400 hover:bg-paper-100 disabled:opacity-25 dark:hover:bg-clay-raised"
                  title="Move up"
                >
                  <ChevronUp className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => moveSlideDown(idx)}
                  disabled={idx === slides.length - 1}
                  className="rounded p-0.5 text-ink-400 hover:bg-paper-100 disabled:opacity-25 dark:hover:bg-clay-raised"
                  title="Move down"
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => removeSlide(idx)}
                  className="rounded p-0.5 text-terracotta-400 hover:bg-terracotta-50 dark:hover:bg-terracotta-900/20"
                  title="Remove slide"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add image: file upload or URL paste */}
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-rule px-3 py-1.5 text-xs font-medium text-ink-500 hover:bg-paper-100 dark:border-clay-rule dark:hover:bg-clay-raised">
          <Plus className="h-3.5 w-3.5" />
          {uploading ? 'Uploading…' : 'Upload image'}
          <input
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            disabled={uploading}
            className="sr-only"
            onChange={(e) => {
              setUploadError(null);
              const file = e.target.files?.[0];
              if (file) void handleFileUpload(file, 'image');
              e.target.value = '';
            }}
          />
        </label>

        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <input
            type="url"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addByUrl();
              }
            }}
            placeholder="or paste image URL (https://…)"
            className={`${INPUT_CLS} flex-1`}
          />
          <button
            type="button"
            onClick={addByUrl}
            disabled={!urlInput.trim()}
            className="shrink-0 rounded-lg border border-rule px-2 py-1.5 text-xs text-ink-500 hover:bg-paper-100 disabled:opacity-30 dark:border-clay-rule dark:hover:bg-clay-raised"
          >
            Add
          </button>
        </div>
      </div>

      {/* PDF section */}
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-ink-400">PDF (optional)</label>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-rule px-3 py-1.5 text-xs font-medium text-ink-500 hover:bg-paper-100 dark:border-clay-rule dark:hover:bg-clay-raised">
            <Plus className="h-3.5 w-3.5" />
            {uploading ? 'Uploading…' : 'Upload PDF'}
            <input
              type="file"
              accept="application/pdf"
              disabled={uploading}
              className="sr-only"
              onChange={(e) => {
                setUploadError(null);
                const file = e.target.files?.[0];
                if (file) void handleFileUpload(file, 'pdf');
                e.target.value = '';
              }}
            />
          </label>
          <input
            type="url"
            value={pdfUrl}
            onChange={(e) => onChangePdfUrl(e.target.value)}
            placeholder="or paste PDF URL (https://…)"
            className={`${INPUT_CLS} flex-1`}
          />
        </div>
        {pdfUrl.startsWith('https://') && (
          <p className="truncate text-xs text-verdant-600 dark:text-verdant-400">
            PDF set: <span className="font-mono">{pdfUrl}</span>
          </p>
        )}
      </div>

      {uploadError && (
        <p className="text-xs text-terracotta-700 dark:text-terracotta-400">{uploadError}</p>
      )}
    </div>
  );
}

// ── Load state ────────────────────────────────────────────────────────────────

type CatalogState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'forbidden' }
  | { phase: 'ready'; tracks: CatalogTrack[] };

// ── Main component ────────────────────────────────────────────────────────────

/** Defensive: treat a missing/non-array `tags` as []. */
function trackTags(t: { tags?: string[] }): string[] {
  return Array.isArray(t.tags) ? t.tags : [];
}

export default function AdminTrails() {
  const [catalogState, setCatalogState] = useState<CatalogState>({ phase: 'loading' });
  const [tick, setTick] = useState(0);
  const forbiddenRef = useRef(false);

  // Client-side audience-tag filter over the already-loaded catalog list.
  // null = no filter ("All"). Mirrors Home.tsx's TrailList activeTag pattern.
  const [activeTag, setActiveTag] = useState<string | null>(null);

  // View: 'list' = track table; 'edit' = create/edit form
  const [view, setView] = useState<'list' | 'edit'>('list');
  // null = new track; CatalogTrack = editing existing
  const [editTrack, setEditTrack] = useState<CatalogTrack | null>(null);
  // id returned after a successful createTrack (for new tracks)
  const [createdId, setCreatedId] = useState<string | null>(null);

  // Metadata form fields
  const [titleField, setTitleField] = useState('');
  const [descField, setDescField] = useState('');
  const [brandField, setBrandField] = useState('');
  const [orderField, setOrderField] = useState('0');
  const [internalField, setInternalField] = useState(false);
  const [publishedField, setPublishedField] = useState(false);
  const [tagsField, setTagsField] = useState('');
  const [badgeIdField, setBadgeIdField] = useState('');
  const [metaSaving, setMetaSaving] = useState(false);
  const [metaError, setMetaError] = useState<string | null>(null);
  const [metaSuccess, setMetaSuccess] = useState(false);

  // Steps editor
  const [localSteps, setLocalSteps] = useState<LocalStep[]>([]);
  const [stepsLoading, setStepsLoading] = useState(false);
  // Sticky: set when a quiz step's content failed to load. While set, saving is
  // DISABLED so a transient/forbidden quiz fetch can never lead to a
  // full-replace PUT that wipes quiz content.
  const [stepsLoadError, setStepsLoadError] = useState<string | null>(null);
  const [stepsSaving, setStepsSaving] = useState(false);
  const [stepsSaveError, setStepsSaveError] = useState<string | null>(null);
  const [stepsStepErrors, setStepsStepErrors] = useState<Record<number, string>>({});
  const [stepsSaveSuccess, setStepsSaveSuccess] = useState(false);

  const reload = useCallback(() => {
    if (!forbiddenRef.current) setTick((t) => t + 1);
  }, []);

  // ── Load catalog ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (forbiddenRef.current) return;
    let cancelled = false;
    setCatalogState({ phase: 'loading' });
    void (async () => {
      const res = await getCatalog();
      if (cancelled || forbiddenRef.current) return;
      if (res.status === 403) {
        forbiddenRef.current = true;
        setCatalogState({ phase: 'forbidden' });
        return;
      }
      if (!res.ok) {
        setCatalogState({
          phase: 'error',
          message:
            res.status === 0
              ? 'Network error — check your connection.'
              : `Failed (HTTP ${res.status}).`,
        });
        return;
      }
      setCatalogState({
        phase: 'ready',
        tracks: Array.isArray(res.data?.tracks) ? res.data.tracks : [],
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  // ── Populate form + load steps when entering edit mode ────────────────────

  useEffect(() => {
    if (view !== 'edit') return;

    setMetaError(null);
    setMetaSuccess(false);
    setStepsLoadError(null);
    setStepsSaveError(null);
    setStepsStepErrors({});
    setStepsSaveSuccess(false);

    if (editTrack === null) {
      // New trail — blank everything
      setTitleField('');
      setDescField('');
      setBrandField('');
      setOrderField('0');
      setInternalField(false);
      setPublishedField(false);
      setTagsField('');
      setBadgeIdField('');
      setLocalSteps([]);
      setCreatedId(null);
      return;
    }

    // Existing trail — populate metadata form
    setTitleField(editTrack.title);
    setDescField(editTrack.description ?? '');
    setBrandField(editTrack.brand ?? '');
    setOrderField(String(editTrack.order ?? 0));
    setInternalField(editTrack.internal ?? false);
    setPublishedField(editTrack.published);
    setTagsField((editTrack.tags ?? []).join(', '));
    setBadgeIdField(editTrack.badgeId ?? '');

    // Build local steps from catalog
    const catalogSteps: Step[] = editTrack.steps ?? [];
    const baseSteps = catalogSteps.map(stepFromCatalog);

    // Quiz steps: fetch questions (prompts/options) from the quiz API.
    // Answers are server-private and NEVER returned — admin must re-select.
    const quizItems = catalogSteps
      .map((s, i) => ({ s, i }))
      .filter(({ s }) => s.type === 'quiz');

    if (quizItems.length === 0) {
      setLocalSteps(baseSteps);
      return;
    }

    let cancelled = false;
    setStepsLoading(true);

    void (async () => {
      const fetched = await Promise.all(
        quizItems.map(async ({ s, i }) => {
          const res = await getQuiz(editTrack.id, s.id);
          return { i, res };
        }),
      );
      if (cancelled) {
        setStepsLoading(false);
        return;
      }

      // Fail CLOSED: if ANY quiz step's content failed to load (transient,
      // 403, etc.), do NOT substitute an empty quiz — that would let a
      // subsequent "Save steps" full-replace wipe real quiz content. Render the
      // structure (base steps) but set a sticky error that disables saving.
      const failedCount = fetched.filter(({ res }) => !res.ok).length;
      if (failedCount > 0) {
        setLocalSteps(baseSteps);
        setStepsLoadError(
          `Could not load quiz content for ${failedCount} paintra${failedCount === 1 ? '' : 's'}. Saving is disabled to avoid wiping quiz data — refresh and reopen this kushti to retry.`,
        );
        setStepsLoading(false);
        return;
      }

      const merged = [...baseSteps];
      for (const { i, res } of fetched) {
        merged[i] = {
          ...merged[i],
          questions: (res.data?.questions ?? []).map((q) => ({
            _key: newKey(),
            id: q.id,
            prompt: q.prompt,
            options: q.options,
            answer: null, // admin must re-select
          })),
        };
      }
      setLocalSteps(merged);
      setStepsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [view, editTrack]);

  // ── Navigation helpers ────────────────────────────────────────────────────

  const openNew = () => {
    setEditTrack(null);
    setView('edit');
  };

  const openEdit = (track: CatalogTrack) => {
    setEditTrack(track);
    setView('edit');
  };

  const backToList = () => {
    setView('list');
    setEditTrack(null);
    setCreatedId(null);
    reload();
  };

  // The track ID we use for steps save (either existing or just-created)
  const currentTrackId = editTrack?.id ?? createdId;

  // ── Metadata save ─────────────────────────────────────────────────────────

  const handleMetaSave = async () => {
    if (!titleField.trim()) {
      setMetaError('Title is required.');
      return;
    }
    setMetaSaving(true);
    setMetaError(null);
    setMetaSuccess(false);

    const tags = tagsField
      .split(',')
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);

    const body: TrackMetaInput = {
      title: titleField.trim(),
      ...(descField.trim() ? { description: descField.trim() } : {}),
      ...(brandField.trim() ? { brand: brandField.trim() } : {}),
      order: parseInt(orderField) || 0,
      internal: internalField,
      published: publishedField,
      ...(tags.length > 0 ? { tags } : {}),
      ...(badgeIdField.trim() ? { badgeId: badgeIdField.trim() } : {}),
    };

    if (currentTrackId) {
      const res = await updateTrack(currentTrackId, body);
      setMetaSaving(false);
      if (res.status === 403) {
        forbiddenRef.current = true;
        setCatalogState({ phase: 'forbidden' });
        return;
      }
      if (!res.ok) {
        setMetaError(
          `Save failed (${(res.data as { error?: string })?.error ?? res.status}).`,
        );
        return;
      }
    } else {
      const res = await createTrack(body);
      setMetaSaving(false);
      if (res.status === 403) {
        forbiddenRef.current = true;
        setCatalogState({ phase: 'forbidden' });
        return;
      }
      if (!res.ok) {
        setMetaError(
          `Create failed (${(res.data as { error?: string })?.error ?? res.status}).`,
        );
        return;
      }
      const newId = res.data?.id;
      if (newId) setCreatedId(newId);
    }

    setMetaSuccess(true);
    setTimeout(() => setMetaSuccess(false), 2500);
  };

  // ── Steps save ────────────────────────────────────────────────────────────

  const handleStepsSave = async () => {
    if (!currentTrackId) return;
    // Reentrancy guard: a fast double-click can't re-enter while a save is in
    // flight (the button is also disabled via `stepsSaving`).
    if (stepsSaving) return;

    // Fail CLOSED: never run a full-replace PUT while quiz content failed to
    // load — that would wipe real quiz data with empty placeholders.
    if (stepsLoadError) {
      setStepsSaveError(stepsLoadError);
      return;
    }

    // Single source of truth for answer integrity: scan the RAW localSteps for
    // any unconfirmed quiz answer BEFORE mapping via toStepInput. A null answer
    // must never reach toStepInput (which would otherwise throw).
    const unansweredIdx = localSteps.findIndex(
      (s) => s.type === 'quiz' && s.questions.some((q) => q.answer === null),
    );
    if (unansweredIdx >= 0) {
      setStepsStepErrors({ [unansweredIdx]: 'answer_unconfirmed' });
      setStepsSaveError(
        'Select the correct answer for every quiz question before saving.',
      );
      return;
    }

    // A slides step must have at least one valid slide image OR a PDF — block the
    // save with a per-step error rather than POST a malformed/empty slides step.
    const emptySlidesIdx = localSteps.findIndex(
      (s) =>
        s.type === 'slides' &&
        s.slides.filter((sl) => SLIDE_SRC_RE.test(sl.src)).length === 0 &&
        !s.pdfUrl.startsWith('https://'),
    );
    if (emptySlidesIdx >= 0) {
      setStepsStepErrors({ [emptySlidesIdx]: 'add_a_slide_or_pdf' });
      setStepsSaveError('Add at least one slide image or a PDF before saving.');
      return;
    }

    setStepsSaving(true);
    setStepsSaveError(null);
    setStepsStepErrors({});
    setStepsSaveSuccess(false);

    // Map defensively: toStepInput throws on a null quiz answer or an empty
    // slides step. The guards above already block both, so this is
    // belt-and-suspenders — never submit malformed data.
    let payload: StepInput[];
    try {
      payload = localSteps.map(toStepInput);
    } catch (err) {
      setStepsSaving(false);
      setStepsSaveError(
        err instanceof Error && err.message === 'slides_empty'
          ? 'Add at least one slide image or a PDF before saving.'
          : 'Select the correct answer for every quiz question before saving.',
      );
      return;
    }

    const res = await putTrackSteps(currentTrackId, payload);
    setStepsSaving(false);

    if (res.status === 403) {
      forbiddenRef.current = true;
      setCatalogState({ phase: 'forbidden' });
      return;
    }

    if (!res.ok) {
      const error = (res.data as { error?: string })?.error ?? '';
      const stepErr = parseStepError(error);
      if (stepErr) {
        setStepsStepErrors({ [stepErr.index]: stepErr.reason });
        setStepsSaveError(
          `Step ${stepErr.index + 1}: ${stepErr.reason.replace(/_/g, ' ')}`,
        );
      } else {
        setStepsSaveError(`Save failed (${error || res.status}).`);
      }
      return;
    }

    setStepsSaveSuccess(true);
    setTimeout(() => setStepsSaveSuccess(false), 2500);
  };

  // ── Step manipulation ─────────────────────────────────────────────────────

  const updateStep = (idx: number, patch: Partial<LocalStep>) =>
    setLocalSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));

  const addStep = () =>
    setLocalSteps((prev) => [...prev, blankStep('video')]);

  const removeStep = (idx: number) =>
    setLocalSteps((prev) => prev.filter((_, i) => i !== idx));

  const moveUp = (idx: number) =>
    setLocalSteps((prev) => {
      if (idx === 0) return prev;
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });

  const moveDown = (idx: number) =>
    setLocalSteps((prev) => {
      if (idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });

  const addQuestion = (stepIdx: number) =>
    updateStep(stepIdx, {
      questions: [
        ...localSteps[stepIdx].questions,
        // Start UNANSWERED — admin must explicitly select the correct option.
        { _key: newKey(), prompt: '', options: ['', ''], answer: null },
      ],
    });

  const removeQuestion = (stepIdx: number, qIdx: number) =>
    updateStep(stepIdx, {
      questions: localSteps[stepIdx].questions.filter((_, i) => i !== qIdx),
    });

  const updateQuestion = (
    stepIdx: number,
    qIdx: number,
    patch: Partial<LocalQuestion>,
  ) =>
    updateStep(stepIdx, {
      questions: localSteps[stepIdx].questions.map((q, i) =>
        i === qIdx ? { ...q, ...patch } : q,
      ),
    });

  const addRef = (stepIdx: number) =>
    updateStep(stepIdx, {
      references: [
        ...localSteps[stepIdx].references,
        { _key: newKey(), label: '', url: '' },
      ],
    });

  const removeRef = (stepIdx: number, rIdx: number) =>
    updateStep(stepIdx, {
      references: localSteps[stepIdx].references.filter((_, i) => i !== rIdx),
    });

  const updateRef = (stepIdx: number, rIdx: number, patch: Partial<LocalRef>) =>
    updateStep(stepIdx, {
      references: localSteps[stepIdx].references.map((r, i) =>
        i === rIdx ? { ...r, ...patch } : r,
      ),
    });

  // ── Render: forbidden (sticky across views) ───────────────────────────────

  if (catalogState.phase === 'forbidden') {
    return (
      <div
        className="mt-6 rounded-xl border border-warn-500/40 bg-paper-0 p-4 text-sm dark:bg-clay-surface"
        role="alert"
      >
        <p className="font-semibold text-ink-900 dark:text-paper-50">Not authorized</p>
        <p className="mt-1 text-ink-500 dark:text-paper-300">
          This view is for Khalifas (admins) only.
        </p>
      </div>
    );
  }

  // ── Render: list view ─────────────────────────────────────────────────────

  if (view === 'list') {
    const readyTracks = catalogState.phase === 'ready' ? catalogState.tracks : [];
    const allTrackTags = Array.from(new Set(readyTracks.flatMap((t) => trackTags(t))));
    const filteredTracks = activeTag
      ? readyTracks.filter((t) => trackTags(t).includes(activeTag))
      : readyTracks;

    return (
      <div>
        {catalogState.phase === 'loading' && (
          <p className="mt-6 text-sm text-ink-400">Loading…</p>
        )}

        {catalogState.phase === 'error' && (
          <div className="mt-6 text-sm text-terracotta-700">
            {catalogState.message}{' '}
            <button onClick={reload} className="underline">
              Retry
            </button>
          </div>
        )}

        {catalogState.phase === 'ready' && (
          <>
            <div className="mt-5 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-ink-700 dark:text-paper-200">
                {catalogState.tracks.length}{' '}
                {catalogState.tracks.length === 1 ? 'kushti' : 'kushtis'}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={reload}
                  className="rounded-lg border border-rule px-2.5 py-1 text-xs font-medium text-ink-500 hover:bg-paper-100 dark:border-clay-rule dark:hover:bg-clay-raised"
                >
                  Refresh
                </button>
                <button
                  onClick={openNew}
                  className="inline-flex items-center gap-1 rounded-lg bg-terracotta-500 px-3 py-1 text-xs font-medium text-white hover:bg-terracotta-600"
                >
                  <Plus className="h-3.5 w-3.5" /> New kushti
                </button>
              </div>
            </div>

            {allTrackTags.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => setActiveTag(null)}
                  aria-pressed={activeTag === null}
                  className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition focus:outline-none focus:ring-2 focus:ring-terracotta-500 ${
                    activeTag === null
                      ? 'bg-terracotta-500 text-white'
                      : 'bg-paper-100 text-ink-500 hover:bg-paper-200 dark:bg-clay-raised dark:text-paper-300 dark:hover:bg-clay-surface'
                  }`}
                >
                  All
                </button>
                {allTrackTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                    aria-pressed={activeTag === tag}
                    className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition focus:outline-none focus:ring-2 focus:ring-terracotta-500 ${
                      activeTag === tag
                        ? 'bg-terracotta-500 text-white'
                        : 'bg-paper-100 text-ink-500 hover:bg-paper-200 dark:bg-clay-raised dark:text-paper-300 dark:hover:bg-clay-surface'
                    }`}
                  >
                    #{tag}
                  </button>
                ))}
              </div>
            )}

            {catalogState.tracks.length === 0 ? (
              <div className="mt-4 rounded-xl border border-rule bg-paper-0 px-4 py-8 text-center dark:border-clay-rule dark:bg-clay-surface">
                <p className="text-sm text-ink-500 dark:text-paper-300">
                  No kushtis yet — create one above.
                </p>
              </div>
            ) : filteredTracks.length === 0 ? (
              <div className="mt-4 rounded-xl border border-rule bg-paper-0 px-4 py-8 text-center dark:border-clay-rule dark:bg-clay-surface">
                <p className="text-sm text-ink-500 dark:text-paper-300">
                  No kushtis match #{activeTag}.
                </p>
              </div>
            ) : (
              <div className="mt-2 overflow-x-auto rounded-xl border border-rule dark:border-clay-rule">
                <table className="w-full text-sm">
                  <thead className="bg-paper-100 text-left text-xs uppercase tracking-wide text-ink-400 dark:bg-clay-raised/40">
                    <tr>
                      <th className="px-3 py-2 font-medium">Title</th>
                      <th className="px-3 py-2 font-medium">Brand</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-3 py-2 font-medium">Paintras</th>
                      <th className="px-3 py-2 font-medium">Tags</th>
                      <th className="px-3 py-2 font-medium">Order</th>
                      <th className="px-3 py-2 font-medium" />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTracks.map((track) => (
                      <tr
                        key={track.id}
                        className="border-t border-rule dark:border-clay-rule"
                      >
                        <td className="px-3 py-2 font-medium text-ink-900 dark:text-paper-50">
                          {track.title}
                        </td>
                        <td className="px-3 py-2 text-xs text-ink-500 dark:text-paper-300">
                          {track.brand ?? '—'}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {track.published ? (
                              <span className="inline-flex rounded-full bg-verdant-200/60 px-2 py-0.5 text-xs font-semibold text-verdant-700 dark:bg-verdant-700/20 dark:text-verdant-300">
                                Published
                              </span>
                            ) : (
                              <span className="inline-flex rounded-full bg-paper-100 px-2 py-0.5 text-xs font-semibold text-ink-400 dark:bg-clay-raised dark:text-paper-300">
                                Draft
                              </span>
                            )}
                            {track.internal && (
                              <span className="inline-flex rounded-full bg-terracotta-100 px-2 py-0.5 text-xs font-semibold text-terracotta-700 dark:bg-terracotta-900/30 dark:text-terracotta-300">
                                Internal
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 tabular-nums text-ink-500 dark:text-paper-300">
                          {track.stepCount}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {(track.tags ?? []).map((tag) => (
                              <span
                                key={tag}
                                className="inline-flex rounded-full bg-paper-100 px-2 py-0.5 text-xs text-ink-500 dark:bg-clay-raised dark:text-paper-300"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-3 py-2 tabular-nums text-ink-500 dark:text-paper-300">
                          {track.order}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => openEdit(track)}
                            className="text-xs text-terracotta-600 hover:underline"
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // ── Render: edit view ─────────────────────────────────────────────────────

  return (
    <div className="mt-4">
      <button
        onClick={backToList}
        className="inline-flex items-center gap-1 text-sm text-ink-400 hover:text-terracotta-600"
      >
        <ArrowLeft className="h-4 w-4" /> All kushtis
      </button>

      <h2 className="mt-3 text-base font-semibold text-ink-900 dark:text-paper-50">
        {editTrack ? `Edit: ${editTrack.title}` : 'New kushti'}
      </h2>

      {/* ── Metadata section ── */}
      <div className="mt-4 rounded-xl border border-rule bg-paper-0 p-4 dark:border-clay-rule dark:bg-clay-surface">
        <h3 className="text-sm font-semibold text-ink-700 dark:text-paper-200">
          Kushti details
        </h3>

        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {/* Title (full width) */}
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-ink-500 dark:text-paper-300">
              Title <span className="text-terracotta-500">*</span>
            </label>
            <input
              type="text"
              value={titleField}
              onChange={(e) => setTitleField(e.target.value)}
              placeholder="Kushti title"
              className="mt-1 w-full rounded-lg border border-rule bg-paper-0 px-3 py-1.5 text-sm text-ink-900 placeholder:text-ink-300 focus:outline-none focus:ring-1 focus:ring-terracotta-500 dark:border-clay-rule dark:bg-clay-floor dark:text-paper-50 dark:placeholder:text-ink-500"
            />
          </div>

          {/* Description (full width) */}
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-ink-500 dark:text-paper-300">
              Description{' '}
              <span className="font-normal text-ink-400">(optional)</span>
            </label>
            <textarea
              value={descField}
              onChange={(e) => setDescField(e.target.value)}
              rows={2}
              placeholder="Short description shown to learners"
              className="mt-1 w-full rounded-lg border border-rule bg-paper-0 px-3 py-1.5 text-sm text-ink-900 placeholder:text-ink-300 focus:outline-none focus:ring-1 focus:ring-terracotta-500 dark:border-clay-rule dark:bg-clay-floor dark:text-paper-50 dark:placeholder:text-ink-500"
            />
          </div>

          {/* Brand */}
          <div>
            <label className="block text-xs font-medium text-ink-500 dark:text-paper-300">
              Brand{' '}
              <span className="font-normal text-ink-400">(optional)</span>
            </label>
            <input
              type="text"
              value={brandField}
              onChange={(e) => setBrandField(e.target.value)}
              placeholder="e.g. Henoyo"
              className="mt-1 w-full rounded-lg border border-rule bg-paper-0 px-3 py-1.5 text-sm text-ink-900 placeholder:text-ink-300 focus:outline-none focus:ring-1 focus:ring-terracotta-500 dark:border-clay-rule dark:bg-clay-floor dark:text-paper-50 dark:placeholder:text-ink-500"
            />
          </div>

          {/* Badge ID */}
          <div>
            <label className="block text-xs font-medium text-ink-500 dark:text-paper-300">
              Badge ID{' '}
              <span className="font-normal text-ink-400">(optional)</span>
            </label>
            <input
              type="text"
              value={badgeIdField}
              onChange={(e) => setBadgeIdField(e.target.value)}
              placeholder="e.g. badge-onboarding"
              className="mt-1 w-full rounded-lg border border-rule bg-paper-0 px-3 py-1.5 text-sm text-ink-900 placeholder:text-ink-300 focus:outline-none focus:ring-1 focus:ring-terracotta-500 dark:border-clay-rule dark:bg-clay-floor dark:text-paper-50 dark:placeholder:text-ink-500"
            />
          </div>

          {/* Order */}
          <div>
            <label className="block text-xs font-medium text-ink-500 dark:text-paper-300">
              Order (lower = first)
            </label>
            <input
              type="number"
              value={orderField}
              onChange={(e) => setOrderField(e.target.value)}
              className="mt-1 w-full rounded-lg border border-rule bg-paper-0 px-3 py-1.5 text-sm text-ink-900 focus:outline-none focus:ring-1 focus:ring-terracotta-500 dark:border-clay-rule dark:bg-clay-floor dark:text-paper-50"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-xs font-medium text-ink-500 dark:text-paper-300">
              Tags{' '}
              <span className="font-normal text-ink-400">(comma-separated)</span>
            </label>
            <input
              type="text"
              value={tagsField}
              onChange={(e) => setTagsField(e.target.value)}
              placeholder="e.g. onboarding, product"
              className="mt-1 w-full rounded-lg border border-rule bg-paper-0 px-3 py-1.5 text-sm text-ink-900 placeholder:text-ink-300 focus:outline-none focus:ring-1 focus:ring-terracotta-500 dark:border-clay-rule dark:bg-clay-floor dark:text-paper-50 dark:placeholder:text-ink-500"
            />
          </div>

          {/* Toggles */}
          <div className="flex items-center gap-6 sm:col-span-2">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-700 dark:text-paper-200">
              <input
                type="checkbox"
                checked={publishedField}
                onChange={(e) => setPublishedField(e.target.checked)}
                className="h-4 w-4 accent-terracotta-500"
              />
              Published
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-700 dark:text-paper-200">
              <input
                type="checkbox"
                checked={internalField}
                onChange={(e) => setInternalField(e.target.checked)}
                className="h-4 w-4 accent-terracotta-500"
              />
              Internal only (staff)
            </label>
          </div>
        </div>

        {metaError && (
          <p className="mt-2 text-xs text-terracotta-700 dark:text-terracotta-400">
            {metaError}
          </p>
        )}
        {metaSuccess && (
          <p className="mt-2 text-xs font-medium text-verdant-600 dark:text-verdant-300">
            Saved
          </p>
        )}

        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => void handleMetaSave()}
            disabled={metaSaving}
            className="rounded-lg bg-terracotta-500 px-4 py-1.5 text-sm font-medium text-white transition hover:bg-terracotta-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {metaSaving
              ? 'Saving…'
              : currentTrackId
                ? 'Save details'
                : 'Create kushti'}
          </button>
          {!currentTrackId && (
            <span className="text-xs text-ink-400">
              Create the kushti first, then add paintras below.
            </span>
          )}
        </div>
      </div>

      {/* ── Steps section (only once track has an ID) ── */}
      {currentTrackId && (
        <div className="mt-4 rounded-xl border border-rule bg-paper-0 p-4 dark:border-clay-rule dark:bg-clay-surface">
          <h3 className="text-sm font-semibold text-ink-700 dark:text-paper-200">
            Paintras{localSteps.length > 0 ? ` (${localSteps.length})` : ''}
          </h3>
          <p className="mt-0.5 text-xs text-ink-400">
            "Save paintras" replaces all paintras on this kushti — use ↑ ↓ to reorder.
          </p>

          {stepsLoadError && (
            <div
              className="mt-3 rounded-lg border border-terracotta-300/60 bg-terracotta-50/60 px-3 py-2 text-xs text-terracotta-700 dark:border-terracotta-700/40 dark:bg-terracotta-900/20 dark:text-terracotta-300"
              role="alert"
            >
              {stepsLoadError}
            </div>
          )}

          {stepsLoading ? (
            <p className="mt-3 text-xs text-ink-400">Loading paintra data…</p>
          ) : (
            <>
              <div className="mt-3 flex flex-col gap-3">
                {localSteps.map((step, idx) => (
                  <StepRow
                    key={step._key}
                    step={step}
                    idx={idx}
                    totalCount={localSteps.length}
                    stepError={stepsStepErrors[idx]}
                    trackId={currentTrackId!}
                    onChange={(patch) => updateStep(idx, patch)}
                    onMoveUp={() => moveUp(idx)}
                    onMoveDown={() => moveDown(idx)}
                    onRemove={() => removeStep(idx)}
                    onAddQuestion={() => addQuestion(idx)}
                    onRemoveQuestion={(qIdx) => removeQuestion(idx, qIdx)}
                    onUpdateQuestion={(qIdx, patch) => updateQuestion(idx, qIdx, patch)}
                    onAddRef={() => addRef(idx)}
                    onRemoveRef={(rIdx) => removeRef(idx, rIdx)}
                    onUpdateRef={(rIdx, patch) => updateRef(idx, rIdx, patch)}
                  />
                ))}
              </div>

              <button
                type="button"
                onClick={addStep}
                className="mt-3 inline-flex items-center gap-1 rounded-lg border border-rule px-3 py-1.5 text-xs font-medium text-ink-500 hover:bg-paper-100 dark:border-clay-rule dark:hover:bg-clay-raised"
              >
                <Plus className="h-3.5 w-3.5" /> Add paintra
              </button>

              {stepsSaveError && (
                <p className="mt-2 text-xs text-terracotta-700 dark:text-terracotta-400">
                  {stepsSaveError}
                </p>
              )}
              {stepsSaveSuccess && (
                <p className="mt-2 text-xs font-medium text-verdant-600 dark:text-verdant-300">
                  Paintras saved
                </p>
              )}

              {localSteps.length > 0 && (
                <button
                  type="button"
                  onClick={() => void handleStepsSave()}
                  disabled={stepsSaving || stepsLoadError !== null}
                  className="mt-3 rounded-lg bg-terracotta-500 px-4 py-1.5 text-sm font-medium text-white transition hover:bg-terracotta-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {stepsSaving
                    ? 'Saving paintras…'
                    : `Save ${localSteps.length} paintra${localSteps.length === 1 ? '' : 's'}`}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
